import { TypologyAndCriteriaData } from '@/api/theHive/criteriaGroups/types';
import {
  convertBillingUnit,
  convertReportType,
  convertTypologyCategory,
  convertTypologyPropertyType,
  convertTypologyRuleKeyword,
  TypologyCategory,
  TypologyPriceType,
  TypologyRuleKeyword,
} from '@/api/theHive/typology/enumerations';
import { ListTypologyData } from '@/api/theHive/typology/types';
import { formatFirstLetter, formatReal } from '@/helpers';

export const presentCriteriasHeander = [
  { id: 1, label: 'Código' },
  { id: 2, label: 'Tipo de Serviço' },
  { id: 3, label: 'Dimensão' },
  { id: 4, label: 'Tipo de Laudo / Relatório' },
  { id: 5, label: 'Valor' },
  { id: 6, label: 'Prazo' },
];

const formatArea = ({
  ruleKeyword,
  firstRule,
  secondRule,
}: {
  ruleKeyword: TypologyRuleKeyword;
  firstRule: number | null;
  secondRule: number | null;
}): string => {
  // regra de negócio (catálogo v2 §3.6): "a partir de 1m²" (ou menos) significa
  // todos os tamanhos — checado antes do guard vazio para "over 0" também pegar
  if (
    ruleKeyword === TypologyRuleKeyword.OVER &&
    firstRule !== null &&
    firstRule <= 1
  ) {
    return 'todos os tamanhos';
  }

  if (!firstRule && !secondRule) return '-';

  const translatedRuleKeyword = convertTypologyRuleKeyword(ruleKeyword);

  if (ruleKeyword === TypologyRuleKeyword.BETWEEN && firstRule && secondRule) {
    return `${translatedRuleKeyword} ${firstRule}m² e ${secondRule}m²`;
  }

  return `${translatedRuleKeyword} ${firstRule}m²`;
};

const formatPrice = (typologyGroup: ListTypologyData): string => {
  const {
    price,
    price_type: priceType,
    billing_unit: billingUnit,
  } = typologyGroup;
  if (!Number.isFinite(price)) return '-';

  const formattedPrice = formatReal(price);
  switch (priceType) {
    case TypologyPriceType.QHT:
      return `${formattedPrice} / hora (QHT)`;
    case TypologyPriceType.VALOR_N:
      return `V1 + N × ${formattedPrice}`;
    default: {
      const unitLabel = billingUnit ? convertBillingUnit(billingUnit) : '';
      return unitLabel ? `${formattedPrice} / ${unitLabel}` : formattedPrice;
    }
  }
};

const formatDeadline = (typologyGroup: ListTypologyData): string => {
  if (typologyGroup.deadline_by_project) return 'Definido por projeto';
  const days = typologyGroup.sla_limit_days;
  if (!days) return '-';
  const daysKind = typologyGroup.sla_use_business_days
    ? 'dias úteis'
    : 'dias corridos';
  return `${days} ${daysKind}`;
};

export interface IServiceSubRow {
  serviceLabel: string;
  dimension: string;
}

export interface IServiceRow {
  id: number;
  code: string;
  reportType: string;
  price: string;
  deadline: string;
  subRows: IServiceSubRow[];
  service: ListTypologyData;
}

export interface ICategoryTable {
  category: TypologyCategory | '';
  categoryLabel: string;
  rows: IServiceRow[];
}

const presentServiceRow = (typologyGroup: ListTypologyData): IServiceRow => {
  // tipos que compartilham a mesma regra de dimensão viram uma única sub-linha
  const subRowsByRule = new Map<string, IServiceSubRow>();

  (typologyGroup.typologies ?? []).forEach((typology) => {
    const dimension = formatArea({
      ruleKeyword: typology.rule_keyword,
      firstRule: typology.rule_first_value,
      secondRule: typology.rule_second_value ?? null,
    });
    const serviceLabel = formatFirstLetter(
      convertTypologyPropertyType(typology.real_estate_type)
    );

    const first = typology.rule_first_value;
    const second = typology.rule_second_value;
    const ruleKey = `${typology.rule_keyword}-${first}-${second}`;
    const existing = subRowsByRule.get(ruleKey);
    if (existing) {
      existing.serviceLabel = `${existing.serviceLabel} · ${serviceLabel}`;
    } else {
      subRowsByRule.set(ruleKey, { serviceLabel, dimension });
    }
  });

  const subRows = Array.from(subRowsByRule.values());
  if (subRows.length === 0) {
    subRows.push({ serviceLabel: typologyGroup.name, dimension: '-' });
  }

  return {
    id: typologyGroup.id,
    code: typologyGroup.code ?? '-',
    reportType: typologyGroup.report_type
      ? convertReportType(typologyGroup.report_type)
      : '-',
    price: formatPrice(typologyGroup),
    deadline: formatDeadline(typologyGroup),
    subRows,
    service: typologyGroup,
  };
};

export const presentCategoriesTableData = (
  data: TypologyAndCriteriaData
): ICategoryTable[] => {
  if (!data?.typology_groups?.length) return [];

  const tablesByCategory = new Map<TypologyCategory | '', ICategoryTable>();

  data.typology_groups.forEach((typologyGroup) => {
    const category = typologyGroup.category ?? '';
    const existing = tablesByCategory.get(category);
    const row = presentServiceRow(typologyGroup);
    if (existing) {
      existing.rows.push(row);
    } else {
      tablesByCategory.set(category, {
        category,
        categoryLabel: typologyGroup.category
          ? convertTypologyCategory(typologyGroup.category)
          : '',
        rows: [row],
      });
    }
  });

  return Array.from(tablesByCategory.values());
};
